// src/pages/Company/CompanyPage.jsx

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Container } from "react-bootstrap";
import Swal from "sweetalert2";
import { formatDateTime } from "../../utils/formatters";
import PaginatedTable from "../Common/PaginatedTable";
import {
  getCompanies,
  deleteCompany,
  updateCompany,
} from "../../services/companyService";
import {
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Building2,
  Eye,
} from "lucide-react";
import EntityDetailsSidebar from "../../components/Common/EntityDetailsSidebar";
import usePermission from "../../hooks/usePermission";
import Unauthorized from "../../components/Common/Unauthorized";

const CompanyPage = () => {
  const navigate = useNavigate();
  const hasPermission = usePermission();
  const [companies, setCompanies] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);

  const schema = [
    { label: "Razón Social", key: "legalName" },
    { label: "Email", key: "email" },
    { label: "NIT", key: "nit" },
    { label: "Teléfono", key: "phoneNumber" },
    { label: "Creada", key: "createdAt", render: formatDateTime },
    { label: "Actualizada", key: "updatedAt", render: formatDateTime },
  ];

  const fetchCompanies = useCallback(async (page, size, search = "") => {
    try {
      const response = await getCompanies(page, size, search);
      if (response.success) {
        setCompanies(response.data.items);
        setTotalRows(response.data.totalCount);
      }
    } catch (err) {
      Swal.fire("Error", "No se pudieron cargar las empresas", "error");
    }
  }, []);

  useEffect(() => {
    fetchCompanies(currentPage, perPage, searchTerm);
  }, [fetchCompanies, currentPage, perPage, searchTerm]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleEdit = (id) => navigate(`/dashboard/company/edit/${id}`);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará la empresa.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteCompany(id);
        if (response.success) {
          Swal.fire("Eliminado", "La empresa ha sido eliminada.", "success");
          fetchCompanies(currentPage, perPage, searchTerm);
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire("Error", "No se pudo eliminar la empresa", "error");
      }
    }
  };

  if (!hasPermission("companies.view")) return <Unauthorized />;

  const columns = [
    { name: "Nombre", selector: (row) => row.name, sortable: true, wrap: true },
    {
      name: "Razón Social",
      selector: (row) => row.legalName,
      sortable: true,
      wrap: true,
    },
    { name: "Email", selector: (row) => row.email, wrap: true },
    {
      name: "Activo",
      center: true,
      cell: (row) =>
        hasPermission("companies.update") ? (
          <Button
            variant="light"
            size="sm"
            className="d-flex align-items-center gap-1 border-0"
            title={row.isActive ? "Desactivar empresa" : "Activar empresa"}
            onClick={async (e) => {
              e.stopPropagation();
              const result = await Swal.fire({
                title: row.isActive
                  ? "¿Deseas inactivar esta empresa?"
                  : "¿Deseas activar esta empresa?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Sí, confirmar",
                cancelButtonText: "Cancelar",
              });

              if (result.isConfirmed) {
                try {
                  const updated = { ...row, isActive: !row.isActive };
                  const response = await updateCompany(updated);
                  if (response.success) {
                    Swal.fire(
                      "Éxito",
                      "Estado actualizado correctamente",
                      "success"
                    );
                    fetchCompanies(currentPage, perPage, searchTerm);
                  } else throw new Error(response.message);
                } catch (err) {
                  Swal.fire(
                    "Error",
                    "No se pudo actualizar el estado",
                    "error"
                  );
                }
              }
            }}
          >
            {row.isActive ? (
              <>
                <CheckCircle size={16} className="text-success" /> Activo
              </>
            ) : (
              <>
                <XCircle size={16} className="text-danger" /> Inactivo
              </>
            )}
          </Button>
        ) : row.isActive ? (
          <span className="text-success fw-semibold">Activo</span>
        ) : (
          <span className="text-danger fw-semibold">Inactivo</span>
        ),
      wrap: true,
      width: "150px",
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="d-flex gap-2">
          {hasPermission("companies.update") && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.companyId);
              }}
              title="Editar"
            >
              <Pencil size={16} />
            </Button>
          )}
          {hasPermission("companies.delete") && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.companyId);
              }}
              title="Eliminar"
            >
              <Trash2 size={16} />
            </Button>
          )}
          <Button
            variant="outline-info"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCompany(row);
            }}
            title="Ver detalles"
          >
            <Eye size={16} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      wrap: true,
      width: "150px",
      center: true,
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-dark">Empresas registradas</h4>
          {hasPermission("companies.create") && (
            <Button onClick={() => navigate("/dashboard/company/create")}>
              + Crear nueva empresa
            </Button>
          )}
        </div>

        <PaginatedTable
          keyField="companyId"
          columns={columns}
          data={companies}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerRowsChange}
          onSearch={handleSearch}
        />

        <EntityDetailsSidebar
          isVisible={!!selectedCompany}
          onClose={() => setSelectedCompany(null)}
          title="Detalles de la empresa"
          icon={<Building2 size={20} />}
          data={selectedCompany}
          schema={schema}
        />
      </Card>
    </div>
  );
};

export default CompanyPage;
